import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  UploadSimple, DownloadSimple, CheckCircle, XCircle, Clock, FileText, Warning, Eye, X,
} from "@phosphor-icons/react";

const TYPE_LABEL = {
  interview_scheduled: "Interview Scheduled",
  interview_confirmed: "Confirmed Interview",
  placement: "Placement",
};

const STATUS_CLS = {
  "Pending Team Leader Review": "bg-amber-50 text-amber-700 border-amber-200",
  "Pending HR Approval": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  "Resubmission Required": "bg-orange-50 text-orange-700 border-orange-200",
  // legacy fallback (pre-migration DB rows)
  "Pending SDL": "bg-amber-50 text-amber-700 border-amber-200",
  "Pending HR": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
};

const STATUS_ORDER = [
  "Pending Team Leader Review",
  "Pending HR Approval",
  "Resubmission Required",
  "Approved",
  "Rejected",
];

const STAGE_STEP = {
  "Pending Team Leader Review": 1,
  "Pending HR Approval": 2,
  Approved: 3,
  Rejected: -1,
  "Resubmission Required": 0,
};

const REQUIRED_LABEL = {
  confirmation_email: "Interview confirmation email",
  offer_letter: "Offer letter",
  offer_supporting: "Supporting proof",
  acceptance_email: "Offer acceptance email",
};

/* Phase 10 — Summary cards shown at the top of Approvals for Group Leads. */
function GroupLeadSummaryCards({ onFilter }) {
  const [counts, setCounts] = useState({ tl_review: 0, hr_approval: 0, resub: 0 });
  useEffect(() => {
    (async () => {
      try {
        const [a, b, c] = await Promise.all([
          api.get(`/incentives/records?status=${encodeURIComponent("Pending Group Lead Approval")}`),
          api.get(`/incentives/records?status=${encodeURIComponent("Pending HR Approval")}`),
          api.get(`/incentives/records?status=${encodeURIComponent("Resubmission Required")}`),
        ]);
        setCounts({ tl_review: (a.data || []).length, hr_approval: (b.data || []).length, resub: (c.data || []).length });
      } catch { /* silent */ }
    })();
  }, []);
  const Card = ({ label, value, tone, filter, testid }) => (
    <button onClick={() => onFilter(filter)} data-testid={testid}
            className={`text-left bg-white border rounded-lg p-4 hover:shadow-sm transition ${tone}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-3xl font-semibold mt-2 text-slate-900">{value}</div>
      <div className="text-xs text-sky-600 mt-2">Click to open queue →</div>
    </button>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="gl-summary-cards">
      <Card testid="gl-card-tl-review" label="Pending Reviews (Your Queue)" value={counts.tl_review}
            filter="Pending Group Lead Approval" tone="border-amber-200" />
      <Card testid="gl-card-hr" label="Forwarded to HR" value={counts.hr_approval}
            filter="Pending HR Approval" tone="border-indigo-200" />
      <Card testid="gl-card-resub" label="Awaiting Resubmission" value={counts.resub}
            filter="Resubmission Required" tone="border-orange-200" />
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const role = user?.role;
  const isExecutive = role === "executive" || role === "sde";
  const isTeamLead = role === "team_lead" || role === "sdl";        // Phase 10 — Manager + Group Lead approvers
  const isTLSupervisor = role === "senior_executive";               // Team Leader = supervisor, no approve
  const isHR = role === "hr";
  const isGroupLead = role === "sdl";
  const [rows, setRows] = useState([]);
  // Sensible default filter per role — TL lands on their action queue; Executive on resubmission queue.
  const defaultStatus =
    isExecutive ? "Resubmission Required"
    : isGroupLead ? "Pending Group Lead Approval"
    : isTeamLead ? "Pending Team Leader Review"
    : isHR ? "Pending HR Approval"
    : "Pending Team Leader Review";
  const [status, setStatus] = useState(defaultStatus);
  const [type, setType] = useState("");
  // Role-scoped chip list — GL sees "Pending Group Lead Approval" (their DB status)
  // instead of the shared "Pending Team Leader Review" chip that would show an empty table.
  const statusChips = useMemo(() => (isGroupLead
    ? ["Pending Group Lead Approval", "Pending HR Approval", "Resubmission Required", "Approved", "Rejected"]
    : STATUS_ORDER), [isGroupLead]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploadType, setUploadType] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);   // { id, name, linkedFrom, contentType }

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (type) params.set("type", type);
    try {
      const { data } = await api.get(`/incentives/records?${params.toString()}`);
      setRows(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  }, [status, type]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (rid) => {
    setOpenId(rid);
    setRemarks("");
    try {
      const { data } = await api.get(`/incentives/records/${rid}`);
      setDetail(data);
      setUploadType(data.required_docs?.find((d) => !(data.documents || []).some((x) => x.doc_type === d)) || data.required_docs?.[0] || "supporting");
    } catch (ex) {
      const msg = formatApiError(ex.response?.data?.detail) || (ex.response?.status === 403 ? "You don't have permission to open this record" : "Failed to open record");
      toast.error(msg);
      setOpenId(null); setDetail(null);
    }
  };

  const uploadDoc = async (file) => {
    if (!file || !openId || !uploadType) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("doc_type", uploadType); fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/incentives/records/${openId}/upload`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("Document uploaded");
      openDetail(openId); load();
    } catch (ex) { toast.error(ex.message); } finally { setBusy(false); }
  };

  const download = async (did, name, linkedFrom) => {
    try {
      // Documents linked from the Offer live in the candidate document repo, not incentive docs.
      const url = linkedFrom === "offer"
        ? `/documents/${did}/download`
        : `/incentives/documents/${did}/download`;
      const res = await api.get(url, { responseType: "blob" });
      const objectUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = objectUrl; a.download = name || did; a.click();
      URL.revokeObjectURL(objectUrl);
    } catch { toast.error("Download failed"); }
  };

  const doAction = async (endpoint, requiresRemarks = false) => {
    if (requiresRemarks && !remarks.trim()) {
      toast.error("Remarks are required"); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/incentives/records/${openId}/${endpoint}`, { remarks: remarks.trim() });
      toast.success(`Status → ${r.data.status || "updated"}`);
      setOpenId(null); setDetail(null); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canApprove = useMemo(() => {
    if (!detail) return false;
    if (!["Pending Group Lead Approval", "Pending Team Leader Review", "Pending HR Approval", "Resubmission Required"].includes(detail.status)) return false;
    const missing = (detail.required_docs || []).filter((d) => !(detail.documents || []).some((x) => x.doc_type === d));
    return missing.length === 0;
  }, [detail]);

  const missingDocs = useMemo(() =>
    detail ? (detail.required_docs || []).filter((d) => !(detail.documents || []).some((x) => x.doc_type === d)) : [],
    [detail]);

  const offerAttachments = useMemo(() =>
    (detail?.documents || []).filter((x) => x.linked_from === "offer"),
    [detail]);

  // Backend-authoritative permission model is enforced server-side. UI mirrors it purely for UX.
  //   - Executive owner: acts ONLY on Resubmission Required (own record). Read-only elsewhere.
  //     Executive resubmit → Pending Team Leader Review.
  //   - Team Leader (team_lead / sdl of same team OR explicit approver):
  //     * On Pending Team Leader Review — Approve → HR, Send Back → Resubmission Required.
  //     * On Resubmission Required — Edit / Upload / Resubmit directly to HR.
  //     * Read-only on Pending HR Approval, Approved, Rejected.
  //   - HR: acts ONLY on Pending HR Approval (Approve / Reject / Send Back).
  //   - Admin: superuser everywhere.
  const isOwner = !!(user && detail && user.id === detail.employee_id);
  const isTLOfRecord = isTeamLead && (
    detail?.approver_id === user?.id ||
    (!!detail?.team && detail?.team === user?.team)
  );
  const isResubmit = detail?.status === "Resubmission Required";

  // Upload/edit allowed only during Resubmission Required, for Executive owner OR Team Leader of team.
  const canUpload = detail && isResubmit && (
    role === "admin" || (isOwner && role === "executive") || isTLOfRecord
  );
  // "Resubmit" button — both Executive owner and Team Leader can trigger it.
  const canResubmit = canUpload;
  const isPendingGL = ["Pending Group Lead Approval", "Pending Team Leader Review"].includes(detail?.status);
  const canTLAct = isPendingGL && (role === "admin" || isTLOfRecord);
  const canHRAct = detail?.status === "Pending HR Approval" && ["admin", "hr"].includes(role);
  // Send-back is authorised at both TL and HR stages; Reject only at HR stage.
  const canSendBack = canTLAct || canHRAct;
  const canReject = canHRAct;
  // Team Leaders (senior_executive) are supervisors — they never act on approvals.
  const canAct = !isTLSupervisor && (canResubmit || canTLAct || canHRAct);

  const nextStageEndpoint =
    isPendingGL ? "tl-approve" :
    detail?.status === "Pending HR Approval" ? "hr-approve" : null;
  const nextStageLabel =
    isPendingGL ? "Approve → HR" :
    detail?.status === "Pending HR Approval" ? "HR Approve (Final)" : "Approve";
  // What state will the record enter after this user resubmits?
  const resubmitTargetStatus =
    role === "admin" || isTLOfRecord ? "Pending HR Approval" : "Pending Group Lead Approval";

  return (
    <div className="space-y-5" data-testid="approvals-page">
      <div>
        <h2 className="text-2xl font-semibold">
          {isGroupLead ? "Review & Approvals"
           : isTLSupervisor ? "Team Approvals (view-only)"
           : (isExecutive || isTeamLead) ? "Incentives & Resubmissions"
           : "Incentive Approvals"}
        </h2>
        <p className="text-sm text-slate-500">
          {isGroupLead
            ? "Approve, reject, or send back incentives, interview confirmations and offer submissions from your Managed Teams. Approved records auto-forward to HR."
            : isTLSupervisor
            ? "Read-only supervision of your team's pending items. Approvals are handled by Group Leads, Managers and HR."
            : isExecutive
            ? "Track your submitted incentives. Records sent back appear under Resubmission Required — Team Leader can also correct them for you."
            : isTeamLead
            ? "Review Executive submissions and act on the shared Resubmission queue. Manager resubmits go directly to HR."
            : "Workflow: Executive submission → Manager / Group Lead review → HR approval → Approved."}
        </p>
      </div>

      {isGroupLead && <GroupLeadSummaryCards onFilter={setStatus} />}

      <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-md p-3">
        {statusChips.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-sm px-3 py-1.5 rounded-full ${status === s ? "bg-sky-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>
            {s}
          </button>
        ))}
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="filter-type"
                className="ml-auto border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Created</th>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Candidate</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Docs</th>
              <th className="text-left px-3 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No records.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`incentive-row-${r.id}`}>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 font-medium">{r.employee_name}</td>
                <td className="px-3 py-2">{r.candidate_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{TYPE_LABEL[r.type]}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.currency} {(r.amount || 0).toFixed(0)}</td>
                <td className="px-3 py-2 text-xs">
                  {(r.required_docs || []).length > 0 ? (
                    <span className={`inline-flex items-center gap-1 ${r.document_count >= (r.required_docs || []).length ? "text-emerald-700" : "text-amber-700"}`}>
                      <FileText size={12} /> {r.document_count}/{(r.required_docs || []).length} required
                    </span>
                  ) : <span className="text-slate-400">n/a</span>}
                </td>
                <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_CLS[r.status] || STATUS_CLS.Pending}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => openDetail(r.id)} data-testid={`open-incentive-${r.id}`}
                          className="text-xs text-sky-700 hover:text-sky-900">Review →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openId && detail && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <div className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="incentive-review-modal">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg">{TYPE_LABEL[detail.type]}</h3>
                <div className="text-xs text-slate-500">
                  {detail.employee_name} · {detail.candidate_name || "no candidate"} · {detail.currency} {(detail.amount || 0).toFixed(0)}
                </div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_CLS[detail.status] || STATUS_CLS.Pending}`}>{detail.status}</span>
            </div>

            {/* Workflow steps */}
            <div className="flex items-center gap-2 mt-4 text-xs">
              {["Submitted", "Group Lead Approval", "HR Approval", "Approved"].map((label, i) => {
                const step = STAGE_STEP[detail.status];
                const isDone = step >= i;
                const isRejected = detail.status === "Rejected";
                return (
                  <div key={label} className="flex items-center gap-1">
                    <div className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${
                      isRejected ? "bg-red-100 text-red-700" :
                      isDone ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
                    }`}>{i}</div>
                    <span className={isDone && !isRejected ? "text-slate-800" : "text-slate-400"}>{label}</span>
                    {i < 3 && <span className="text-slate-300">→</span>}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 space-y-3">
              {/* Reviewer feedback banner — most important for the Executive when a record is sent back */}
              {detail.status === "Resubmission Required" && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-md p-4" data-testid="reviewer-feedback-banner">
                  <div className="flex items-start gap-2">
                    <Warning size={18} weight="fill" className="text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-orange-900">Sent back for resubmission</div>
                      <div className="text-xs text-orange-700 mt-0.5">
                        by <b>{detail.resubmit_requested_by_name || "reviewer"}</b>
                        {detail.resubmit_requested_at && <> on {new Date(detail.resubmit_requested_at).toLocaleString()}</>}
                      </div>
                      {detail.resubmit_reason && (
                        <div className="mt-2 bg-white/70 border border-orange-200 rounded p-2 text-sm text-slate-800 whitespace-pre-wrap">
                          {detail.resubmit_reason}
                        </div>
                      )}
                      {(isOwner || isTLOfRecord) && (
                        <div className="mt-3 text-xs text-orange-800">
                          {isTLOfRecord && !isOwner
                            ? <>As Team Leader you may Edit / Upload / <b>Resubmit</b> — your resubmission goes directly to <b>Pending HR Approval</b>.</>
                            : <>Upload any missing documents below, add a short note explaining what changed, then click <b>Resubmit</b>. Your resubmission goes to <b>Pending Group Lead Approval</b>.</>
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {detail.status === "Rejected" && (
                <div className="bg-red-50 border-2 border-red-300 rounded-md p-4" data-testid="rejection-banner">
                  <div className="flex items-start gap-2">
                    <XCircle size={18} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-red-900">Rejected</div>
                      <div className="text-xs text-red-700 mt-0.5">
                        by <b>{detail.rejected_by_name}</b> on {detail.rejected_at && new Date(detail.rejected_at).toLocaleString()}
                      </div>
                      {detail.remarks && (
                        <div className="mt-2 bg-white/70 border border-red-200 rounded p-2 text-sm text-slate-800 whitespace-pre-wrap">{detail.remarks}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-xs text-slate-500 mb-2">Source</div>
                <div className="text-sm">
                  <b>{detail.source_type}</b> · {detail.source_ref?.company || "—"}
                  {detail.source_ref?.job_title && ` · ${detail.source_ref.job_title}`}
                </div>
                {detail.offer_ref && (
                  <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600 space-y-0.5">
                    <div><b>Offer:</b> {detail.offer_ref.company} · {detail.offer_ref.job_title}</div>
                    {detail.offer_ref.employment_type && <div>Employment: {detail.offer_ref.employment_type}</div>}
                    {detail.offer_ref.salary && <div>Salary: {detail.offer_ref.salary}</div>}
                    {detail.offer_ref.joining_date && <div>Joining: {detail.offer_ref.joining_date}</div>}
                    {detail.offer_ref.submitted_by_name && <div>Submitted by <b>{detail.offer_ref.submitted_by_name}</b> on {new Date(detail.offer_ref.submitted_at).toLocaleString()}</div>}
                    {detail.offer_ref.notes && <div className="italic">Notes: {detail.offer_ref.notes}</div>}
                  </div>
                )}
              </div>

              {/* Phase 6B: Show all documents linked from the offer as attachments the reviewer can open */}
              {offerAttachments.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Attachments from Offer</div>
                  <div className="space-y-2" data-testid="offer-attachments">
                    {offerAttachments.map((d) => (
                      <div key={d.id} className="flex items-center justify-between border border-emerald-200 bg-emerald-50/40 rounded-md px-3 py-2">
                        <div className="text-sm inline-flex items-center gap-1.5 min-w-0">
                          <FileText size={14} className="text-emerald-700 flex-shrink-0" />
                          <span className="text-[10px] uppercase tracking-widest text-emerald-800 border border-emerald-300 rounded-full px-1.5 py-0.5 flex-shrink-0">
                            {REQUIRED_LABEL[d.doc_type] || d.doc_type}
                          </span>
                          <span className="truncate">{d.original_filename}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <button onClick={() => setPreviewDoc({ id: d.id, name: d.original_filename, linkedFrom: "offer", contentType: d.content_type })}
                                  data-testid={`preview-offer-doc-${d.id}`}
                                  className="text-xs px-2.5 py-1 rounded-md bg-sky-600 hover:bg-sky-700 text-white inline-flex items-center gap-1">
                            <Eye size={12} /> Quick view
                          </button>
                          <button onClick={() => download(d.id, d.original_filename, "offer")}
                                  data-testid={`download-offer-doc-${d.id}`}
                                  title="Download"
                                  className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100">
                            <DownloadSimple size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(detail.required_docs || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Required Supporting Documents</div>
                  <div className="space-y-2">
                    {(detail.required_docs || []).map((rd) => {
                      const uploaded = (detail.documents || []).find((x) => x.doc_type === rd);
                      return (
                        <div key={rd} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2">
                          <div className="text-sm">
                            {uploaded ? <CheckCircle size={14} className="inline text-emerald-600 mr-1" /> : <Warning size={14} className="inline text-amber-600 mr-1" />}
                            {REQUIRED_LABEL[rd] || rd}
                          </div>
                          {uploaded ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => setPreviewDoc({ id: uploaded.id, name: uploaded.original_filename, linkedFrom: uploaded.linked_from, contentType: uploaded.content_type })}
                                      data-testid={`preview-required-${rd}`}
                                      className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-100 inline-flex items-center gap-1">
                                <Eye size={11} /> View
                              </button>
                              {!isResubmit && (
                                <button onClick={() => download(uploaded.id, uploaded.original_filename, uploaded.linked_from)}
                                        title={uploaded.original_filename}
                                        data-testid={`download-required-${rd}`}
                                        className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-600">
                                  <DownloadSimple size={11} />
                                </button>
                              )}
                              {canUpload && (
                                <label className="text-xs cursor-pointer bg-sky-100 hover:bg-sky-200 text-sky-800 rounded-md px-2 py-1 inline-flex items-center gap-1"
                                       data-testid={`replace-${rd}`}
                                       title="Replace this document">
                                  <UploadSimple size={11} /> Replace
                                  <input type="file" className="hidden"
                                         onClick={() => setUploadType(rd)}
                                         onChange={(e) => { setUploadType(rd); uploadDoc(e.target.files?.[0]); }} />
                                </label>
                              )}
                            </div>
                          ) : canUpload ? (
                            <label className="text-xs cursor-pointer bg-sky-600 hover:bg-sky-700 text-white rounded-md px-3 py-1 inline-flex items-center gap-1" data-testid={`upload-${rd}`}>
                              <UploadSimple size={12} /> Upload
                              <input type="file" className="hidden"
                                     onClick={() => setUploadType(rd)}
                                     onChange={(e) => { setUploadType(rd); uploadDoc(e.target.files?.[0]); }} />
                            </label>
                          ) : (
                            <span className="text-xs text-amber-700 inline-flex items-center gap-1" title="Uploads happen during Resubmission Required by Executive or Team Leader.">
                              <Warning size={11} weight="fill" /> awaiting upload
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {["Pending Group Lead Approval", "Pending Team Leader Review", "Pending HR Approval", "Resubmission Required"].includes(detail.status) && (canAct || role === "admin") && (
                <div>
                  <label className="text-xs text-slate-500">Remarks {isResubmit ? "(explanation of what changed)" : "(required for reject / send-back)"}</label>
                  <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                            data-testid="incentive-remarks"
                            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                </div>
              )}

              {detail.tl_approved_by_name && (
                <div className="text-xs text-slate-600 border-l-2 border-indigo-300 pl-3">
                  Team Leader approved by <b>{detail.tl_approved_by_name}</b> on {new Date(detail.tl_approved_at).toLocaleString()}
                  {detail.tl_remarks && <div className="italic mt-0.5">{detail.tl_remarks}</div>}
                </div>
              )}
              {detail.hr_approved_by_name && (
                <div className="text-xs text-slate-600 border-l-2 border-emerald-300 pl-3">
                  HR approved by <b>{detail.hr_approved_by_name}</b> on {new Date(detail.hr_approved_at).toLocaleString()}
                  {detail.hr_remarks && <div className="italic mt-0.5">{detail.hr_remarks}</div>}
                </div>
              )}
              {detail.status === "Rejected" && (
                <div className="text-xs text-red-600 border-l-2 border-red-300 pl-3">
                  Rejected by <b>{detail.rejected_by_name}</b> on {new Date(detail.rejected_at).toLocaleString()}
                  {detail.remarks && <div className="italic mt-0.5">{detail.remarks}</div>}
                </div>
              )}
              {detail.status === "Resubmission Required" && (
                <div className="text-xs text-orange-700 border-l-2 border-orange-300 pl-3">
                  Sent back by <b>{detail.resubmit_requested_by_name}</b> on {new Date(detail.resubmit_requested_at).toLocaleString()}
                  {detail.resubmit_reason && <div className="italic mt-0.5">Reason: {detail.resubmit_reason}</div>}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6 items-center">
              {!canAct && role !== "admin" && (
                <span className="text-xs text-slate-500 mr-auto inline-flex items-center gap-1" data-testid="readonly-indicator">
                  <Eye size={12} /> Read-only for your role at this stage
                </span>
              )}
              <button type="button" onClick={() => { setOpenId(null); setDetail(null); }}
                      className="px-4 py-2 text-sm rounded-md border border-slate-200">Close</button>
              {canResubmit && (
                <button onClick={() => doAction("resubmit", true)} disabled={busy || !canApprove || !remarks.trim()}
                        title={!canApprove ? `Upload missing: ${missingDocs.join(", ")}` : !remarks.trim() ? "Add an explanation of what changed" : `Resubmit → ${resubmitTargetStatus}`}
                        data-testid="resubmit-btn"
                        className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white">
                  Resubmit → {resubmitTargetStatus === "Pending HR Approval" ? "HR" : "Manager"}
                </button>
              )}
              {canSendBack && (
                <button onClick={() => doAction("send-back", true)} disabled={busy || !remarks.trim()}
                        title={!remarks.trim() ? "Add a comment explaining what needs to change" : "Send back to Resubmission Required"}
                        data-testid="sendback-btn"
                        className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white">
                  Send back
                </button>
              )}
              {canReject && (
                <button onClick={() => doAction("reject", true)} disabled={busy || !remarks.trim()}
                        data-testid="reject-btn"
                        className="px-4 py-2 text-sm rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white inline-flex items-center gap-1">
                  <XCircle size={14} /> Reject
                </button>
              )}
              {(canTLAct || canHRAct) && nextStageEndpoint && (
                <button onClick={() => doAction(nextStageEndpoint)} disabled={busy || !canApprove}
                        data-testid="approve-btn"
                        title={!canApprove ? `Missing: ${missingDocs.join(", ")}` : nextStageLabel}
                        className="px-4 py-2 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white inline-flex items-center gap-1">
                  <CheckCircle size={14} /> {nextStageLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {previewDoc && (
        <PreviewDrawer doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}

function PreviewDrawer({ doc, onClose }) {
  // Offer-linked docs live in the candidate repo; legacy incentive uploads use their own endpoint.
  const base = process.env.REACT_APP_BACKEND_URL;
  const previewUrl = doc.linkedFrom === "offer"
    ? `${base}/api/documents/${doc.id}/preview`
    : `${base}/api/incentives/documents/${doc.id}/download`;   // legacy fallback (attachment)
  const ct = (doc.contentType || "").toLowerCase();
  const isImage = ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)$/i.test(doc.name || "");
  const isPdf = ct.includes("pdf") || /\.pdf$/i.test(doc.name || "");
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] grid place-items-stretch" data-testid="preview-drawer">
      <div className="ml-auto bg-white h-full w-full max-w-3xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-slate-400">Quick view</div>
            <div className="font-semibold truncate">{doc.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <a href={previewUrl} target="_blank" rel="noreferrer"
               className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50">Open in new tab</a>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 bg-slate-100 overflow-hidden">
          {isPdf && (
            <iframe title={doc.name} src={previewUrl} className="w-full h-full border-0" />
          )}
          {isImage && (
            <div className="w-full h-full overflow-auto grid place-items-center p-4">
              <img alt={doc.name} src={previewUrl} className="max-w-full max-h-full object-contain" />
            </div>
          )}
          {!isPdf && !isImage && (
            <div className="w-full h-full grid place-items-center p-6 text-center">
              <div>
                <div className="text-slate-500 text-sm">Inline preview is not available for this file type.</div>
                <a href={previewUrl} target="_blank" rel="noreferrer"
                   className="mt-3 inline-block text-sm px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white">
                  Open document
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
